import { useCallback, useMemo, useRef } from 'react';
import { AutocompleteValue } from '@mui/material';
import { AutocompleteClasses } from '@mui/material';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Autocomplete, mergeOverrides } from '@lyearn/ui';
import useDebounceState from '@/hooks/useDebounceState';
import { borderRadius6 } from '@/modules/session/utils/styles';

import { asyncMultiSelectopperStyles } from './styles';
import { AsyncSelectProps } from './types';
import { defaultGetOptionLabel, defaultIsOptionEqualToValue } from './utils';

export const CLASSES = {
  popper: asyncMultiSelectopperStyles,
  tag: cn('bg-bg-primary border border-solid border-border', borderRadius6),
} as AutocompleteClasses;

const EMPTY_ARRAY: never[] = [];

const DISTANCE_FROM_END = 10;

function AsyncSelect<
  T,
  Q extends { searchText: string },
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(props: AsyncSelectProps<T, Q, Multiple, DisableClearable, FreeSolo>) {
  const { t } = useTranslation();
  const {
    className,
    multiple,
    useOptionsQuery,
    size = 'large',
    noOptionsText = t('Common:NoOptions'),
    getOptionLabel = defaultGetOptionLabel,
    isOptionEqualToValue = defaultIsOptionEqualToValue,
    queryParams,
    onChange: onChangeProp,
    onInputChange: onInputChangeProp,
    classes,
    value,
    ...rest
  } = props;

  const isUserTypingRef = useRef(false);
  const [inputValue, setInputValue, searchText] = useDebounceState('');
  const defaultEmptyValue = multiple ? EMPTY_ARRAY : null;

  const mergedOverrides = mergeOverrides(CLASSES, classes);
  const { data, fetchMore } = useOptionsQuery({
    ...queryParams,
    searchText: isUserTypingRef.current ? searchText : '',
  } as Q);

  const onInputChange = useCallback(
    (_: any, nextInputValue: string, reason: string) => {
      isUserTypingRef.current = reason === 'input';
      setInputValue(nextInputValue);
      onInputChangeProp?.(nextInputValue);
    },
    [onInputChangeProp, setInputValue],
  );

  const emptyOptionsText = useMemo(
    () => (typeof noOptionsText === 'string' ? noOptionsText : noOptionsText?.(searchText)),
    [noOptionsText, searchText],
  );

  const onChange = useCallback(
    (_: any, value: AutocompleteValue<T, Multiple, DisableClearable, FreeSolo>) => {
      onChangeProp?.(value);
    },
    [onChangeProp],
  );

  return (
    <Autocomplete
      ListboxProps={
        fetchMore
          ? {
              onScroll: (event: React.SyntheticEvent) => {
                const listboxNode = event.currentTarget;
                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >=
                  listboxNode.scrollHeight - DISTANCE_FROM_END
                ) {
                  fetchMore();
                }
              },
            }
          : undefined
      }
      className={className}
      classes={multiple ? mergedOverrides : classes}
      getOptionLabel={getOptionLabel}
      inputValue={inputValue}
      isOptionEqualToValue={isOptionEqualToValue}
      multiple={multiple}
      noOptionsText={emptyOptionsText}
      options={data}
      size={size}
      value={(value ?? defaultEmptyValue) as TSFixMe}
      onChange={onChange}
      onInputChange={onInputChange}
      {...rest}
    />
  );
}

export default AsyncSelect;
